/**
 * The Commissioned Book - won-work analytics off the SharePoint
 * Schedule of Work, with team/client cross-filter drill-down.
 * Clicking a team (pie/legend/table badge) or a client (top-clients
 * bar/table name) re-scopes every KPI, chart and the table. Filters
 * live in the URL (?team=…&client=…) so views are shareable.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle, ArrowDown, ArrowUp, X } from "lucide-react";
import {
  TeamPie, QualQuantMix, TimeToWin, TopClients, MonthlyTrend,
  fmtGbp, fmtGbpShort, fmtDate, teamColor,
} from "./CommissionedPanels";
import { clientKey, buildClientIndex } from "./clientGrouping";

const API = process.env.REACT_APP_BACKEND_URL;

const median = (vals) => {
  if (!vals.length) return null;
  const s = [...vals].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round(((s[mid - 1] + s[mid]) / 2) * 10) / 10;
};

const groupAgg = (items, keyFn) => {
  const g = new Map();
  for (const i of items) {
    const k = keyFn(i);
    const b = g.get(k) || { count: 0, value: 0 };
    b.count += 1;
    b.value += i.value_gbp || 0;
    g.set(k, b);
  }
  return [...g.entries()].map(([k, v]) => ({ key: k, ...v }));
};

function SortBtn({ label, k, sortKey, sortDir, onSort, testid }) {
  const active = sortKey === k;
  const Arrow = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      data-testid={testid}
      className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${active ? "text-[#1B4F8A] font-semibold" : "text-stone-400 hover:text-stone-600"}`}
    >
      {label}
      <Arrow className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
    </button>
  );
}

function FilterChip({ label, onClear, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full pl-3 pr-1.5 py-1"
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        data-testid={`${testid}-clear`}
        className="rounded-full p-0.5 hover:bg-emerald-100 transition-colors"
        title="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export default function CommissionedBook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("com_date");
  const [sortDir, setSortDir] = useState("desc");
  const [searchParams, setSearchParams] = useSearchParams();
  const teamFilter = searchParams.get("team");
  const clientFilter = searchParams.get("client");

  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/commissioned-book`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the commissioned book."))
      .finally(() => setLoading(false));
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key);
    else next.set(key, value);
    if (next.get("team") || next.get("client")) next.set("book", "commissioned");
    else next.delete("book");
    setSearchParams(next, { replace: true });
  };
  const toggleTeam = (t) => setFilter("team", teamFilter === t ? null : t);
  const toggleClient = (c) => setFilter("client", clientFilter === c ? null : c);
  const clearAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    next.delete("client");
    next.delete("book");
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  // Cross-filter scoping: each panel excludes its OWN dimension's
  // filter but applies the other, so you can always hop selection.
  const clientIndex = useMemo(() => buildClientIndex(data?.items || []), [data]);

  const view = useMemo(() => {
    const all = data?.items || [];
    const clientScoped = clientFilter ? all.filter((i) => clientKey(i) === clientFilter) : all;
    const teamScoped = teamFilter ? all.filter((i) => i.team === teamFilter) : all;
    const filtered = teamFilter ? clientScoped.filter((i) => i.team === teamFilter) : clientScoped;

    const byTeam = groupAgg(clientScoped, (i) => i.team)
      .map((g) => ({ team: g.key, count: g.count, value: g.value }))
      .sort((a, b) => b.count - a.count);

    let topClients = groupAgg(teamScoped, clientKey)
      .map((g) => ({
        key: g.key,
        company: clientIndex.get(g.key)?.label || g.key,
        variants: clientIndex.get(g.key)?.variants || [],
        count: g.count,
        value: g.value,
      }))
      .sort((a, b) => b.value - a.value);
    const head = topClients.slice(0, 8);
    if (clientFilter && !head.some((c) => c.key === clientFilter)) {
      const sel = topClients.find((c) => c.key === clientFilter);
      if (sel) head.push(sel);
    }
    topClients = head;

    const qualQuant = groupAgg(filtered, (i) => i.qual_quant || "Unspecified")
      .sort((a, b) => b.count - a.count);

    const withDays = filtered.filter((i) => i.days_to_win != null);
    const days = withDays.map((i) => i.days_to_win);
    const ttw = {
      n: withDays.length,
      avg_days: days.length ? days.reduce((s, d) => s + d, 0) / days.length : null,
      median_days: median(days),
      fastest: withDays.length ? withDays.reduce((a, b) => (a.days_to_win <= b.days_to_win ? a : b)) : null,
      slowest: withDays.length ? withDays.reduce((a, b) => (a.days_to_win >= b.days_to_win ? a : b)) : null,
    };

    const monthKeys = (data?.monthly || []).map((m) => m.month);
    const mg = Object.fromEntries(monthKeys.map((k) => [k, { count: 0, value: 0 }]));
    for (const i of filtered) {
      const k = (i.com_date || "").slice(0, 7);
      if (mg[k]) { mg[k].count += 1; mg[k].value += i.value_gbp || 0; }
    }
    const monthly = monthKeys.map((k) => ({ month: k, ...mg[k] }));

    const values = filtered.filter((i) => i.value_gbp).map((i) => i.value_gbp);
    const bookValue = values.reduce((s, v) => s + v, 0);
    const totals = {
      count: filtered.length,
      book_value: filtered.length ? bookValue : null,
      avg_value: values.length ? bookValue / values.length : null,
      median_days_to_win: ttw.median_days,
    };

    return { filtered, byTeam, topClients, qualQuant, ttw, monthly, totals };
  }, [data, teamFilter, clientFilter, clientIndex]);

  const items = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    return [...view.filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "string" || typeof vb === "string"
        ? String(va || "").localeCompare(String(vb || ""))
        : (va ?? -Infinity) - (vb ?? -Infinity);
      return dir * cmp;
    });
  }, [view.filtered, sortKey, sortDir]);

  const t = view.totals;
  const hasFilter = Boolean(teamFilter || clientFilter);

  return (
    <section
      id="commissioned-book"
      data-testid="commissioned-book"
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm mt-6 scroll-mt-20"
    >
      <header className="px-5 pt-4 pb-3 border-b border-stone-100">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-emerald-700 mb-0.5">
          The commissioned book
        </p>
        <p className="text-sm text-stone-600">
          Every <span className="font-medium text-slate-800">won project</span> in the SharePoint Schedule of Work - who's delivering it, how fast it landed and where the value sits. Click a team or client to drill down.
        </p>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-stone-500 text-sm px-5 py-8" data-testid="commissioned-book-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading the commissioned book…
        </div>
      )}
      {!loading && error && (
        <div className="mx-5 my-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="commissioned-book-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {hasFilter && (
            <div
              data-testid="commissioned-filter-bar"
              className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-emerald-100 bg-emerald-50/40"
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                Drilled into
              </span>
              {teamFilter && (
                <FilterChip
                  label={`Team: ${teamFilter}`}
                  onClear={() => setFilter("team", null)}
                  testid="commissioned-filter-chip-team"
                />
              )}
              {clientFilter && (
                <FilterChip
                  label={`Client: ${clientIndex.get(clientFilter)?.label || clientFilter}`}
                  onClear={() => setFilter("client", null)}
                  testid="commissioned-filter-chip-client"
                />
              )}
              <button
                type="button"
                onClick={clearAll}
                data-testid="commissioned-filter-clear"
                className="text-xs font-semibold text-emerald-800 hover:text-emerald-600 transition-colors ml-1"
              >
                clear all
              </button>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-100">
            {[
              { label: "Commissioned projects", value: t.count?.toLocaleString() ?? "-", testid: "commissioned-book-kpi-count" },
              { label: "Commissioned value", value: fmtGbpShort(t.book_value), tone: "text-emerald-700", testid: "commissioned-book-kpi-value" },
              { label: "Median time-to-win", value: t.median_days_to_win != null ? `${Math.round(t.median_days_to_win)}d` : "-", tone: "text-[#1B4F8A]", testid: "commissioned-book-kpi-median-days" },
              { label: "Avg project value", value: fmtGbpShort(t.avg_value), testid: "commissioned-book-kpi-avg-value" },
            ].map((k) => (
              <div key={k.label} className="bg-white px-4 py-3" data-testid={k.testid}>
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">{k.label}</p>
                <p className={`text-2xl font-medium tabular-nums leading-none ${k.tone || "text-slate-900"}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Charts grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-5 py-4 border-t border-stone-100 bg-stone-50/50">
            <TeamPie byTeam={view.byTeam} selected={teamFilter} onSelect={toggleTeam} />
            <div className="flex flex-col gap-4">
              <TimeToWin ttw={view.ttw} />
              <QualQuantMix mix={view.qualQuant} />
            </div>
            <TopClients clients={view.topClients} selected={clientFilter} onSelect={toggleClient} />
            <div className="md:col-span-2 xl:col-span-3">
              <MonthlyTrend monthly={view.monthly} />
            </div>
          </div>

          {/* Project table */}
          <div className="overflow-x-auto border-t border-stone-100">
            <table className="w-full text-sm" data-testid="commissioned-book-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-2.5 text-left font-medium">Client / project</th>
                  <th className="px-3 py-2.5 text-left font-medium">Team</th>
                  <th className="px-3 py-2.5 text-left font-medium">Mix</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Value" k="value_gbp" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="commissioned-book-sort-value" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">Quoted</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Commissioned" k="com_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="commissioned-book-sort-com-date" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Days to win" k="days_to_win" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="commissioned-book-sort-days" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr
                    key={item.cid ?? i}
                    data-testid={`commissioned-book-row-${item.cid}`}
                    className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleClient(clientKey(item))}
                        data-testid={`commissioned-row-client-${item.cid}`}
                        title={`Drill into ${item.client_company || "Unknown"}`}
                        className="font-medium text-slate-900 leading-snug truncate max-w-[280px] block text-left hover:text-[#1B4F8A] hover:underline transition-colors"
                      >
                        {item.client_company || "Unknown client"}
                      </button>
                      <p className="text-[11px] text-stone-500 truncate max-w-[280px]">
                        {item.topic || item.project_name || ""}
                      </p>
                      <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                        {item.cognito_admin ? (
                          <a
                            href={item.cognito_admin}
                            target="_blank"
                            rel="noreferrer"
                            title="Open in Cognito (admin)"
                            className="hover:text-[#1B4F8A] hover:underline transition-colors"
                          >
                            #{item.cid}
                          </a>
                        ) : (
                          <span>#{item.cid}</span>
                        )}
                        {item.project_number && <span> · {item.project_number}</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleTeam(item.team)}
                        data-testid={`commissioned-row-team-${item.cid}`}
                        title={`Drill into team ${item.team}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700 hover:text-[#1B4F8A] transition-colors"
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: teamColor(item.team, 0) }}
                        />
                        {item.team}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-stone-600">{item.qual_quant}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                      {fmtGbp(item.value_gbp)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500 whitespace-nowrap">
                      {fmtDate(item.proposal_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-slate-700 whitespace-nowrap">
                      {fmtDate(item.com_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-[#1B4F8A]">
                      {item.days_to_win != null ? `${item.days_to_win}d` : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && (
              <p className="px-5 py-8 text-sm text-stone-400" data-testid="commissioned-book-empty">
                {hasFilter
                  ? "No commissioned projects match the current drill-down."
                  : "No commissioned projects in the Schedule of Work yet."}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
